import { useCallback, useMemo } from 'react';
import {
  useApolloClient,
  useMutation,
  useReactiveVar,
  makeVar,
} from '@apollo/client';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import isClient from 'commons/isClient';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import {
  GET_MODULAR_PDP_STATE_CLIENT,
  SET_MODULAR_PDP_SLUG,
  SET_MODULAR_PDP_PRICE,
  SET_MODULAR_PDP_BUNDLE_PRODUCTS,
  SET_MODULAR_PDP_PRECONFIG_PRODUCTS,
  SET_MODULAR_TITLE,
  SET_MODULAR_ORIENTATION_OPTIONS,
  SET_MODULAR_SECTIONAL_PRODUCT,
} from './queries';

const useModularPDPVar = () => {
  const apolloClient = useApolloClient();

  const { data: modularCacheData } = useSuspenseQuery(
    GET_MODULAR_PDP_STATE_CLIENT,
    {
      // TODO: Remove the isNewConsumerApp conditional here as part of
      // migrating modular PDP state to Jotai
      skip: isNewConsumerApp || isClient(),
    }
  );
  const [setModularParentSlug] = useMutation(SET_MODULAR_PDP_SLUG);
  const [setModularTitle] = useMutation(SET_MODULAR_TITLE);
  const [setModularTotalPrice] = useMutation(SET_MODULAR_PDP_PRICE);
  const [setModularOrientation] = useMutation(SET_MODULAR_ORIENTATION_OPTIONS);
  const [setModularSectional] = useMutation(SET_MODULAR_SECTIONAL_PRODUCT);
  const [setModularBundleProducts] = useMutation(
    SET_MODULAR_PDP_BUNDLE_PRODUCTS
  );
  const [setModularPreconfigProducts] = useMutation(
    SET_MODULAR_PDP_PRECONFIG_PRODUCTS
  );

  const modularPDPVar = useMemo(
    () =>
      isClient()
        ? apolloClient.cache?.jbReactiveVars?.modularPDPVar || makeVar(null)
        : makeVar(null),
    [apolloClient.cache?.jbReactiveVars?.modularPDPVar]
  );

  const modularPDPVarState = useReactiveVar(modularPDPVar);
  const modularState = isClient() ? modularPDPVarState : modularCacheData;

  const setModularSlug = slug => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        modularParentSlug: slug,
      });
      return;
    }
    setModularParentSlug({
      variables: {
        modularSlug: slug,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const setModularCollectionTitle = title => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        collectionTitle: title,
      });
      return;
    }
    setModularTitle({
      variables: {
        collectionTitle: title,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const setModularPrice = price => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        modularTotalPrice: price,
      });
      return;
    }
    setModularTotalPrice({
      variables: {
        modularTotalPrice: price,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const setModularBundledProducts = (products = []) => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        bundleProducts: products,
      });
      return;
    }
    setModularBundleProducts({
      variables: {
        bundleProducts: products,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const setModularPreConfigProducts = products => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        preConfiguredProducts: products,
      });
      return;
    }
    setModularPreconfigProducts({
      variables: {
        preConfiguredProducts: products,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const setModularParentOrientationOptions = orientationOpts => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        modularParentOrientationOptions: orientationOpts,
      });
      return;
    }
    setModularOrientation({
      variables: {
        orientationOpts,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const setModularSectionalProduct = sectionalProduct => {
    if (isClient()) {
      modularPDPVar({
        ...modularPDPVar(),
        modularSectionalProduct: sectionalProduct,
      });
      return;
    }
    setModularSectional({
      variables: {
        sectionalProduct,
      },
      refetchQueries: [{ query: GET_MODULAR_PDP_STATE_CLIENT }],
    });
  };

  const updateCurrentModularGalleryProduct = updatedProduct => {
    if (!isClient() || !updatedProduct) {
      return;
    }
    modularPDPVar({
      ...modularPDPVar(),
      currentModularGalleryProduct: updatedProduct,
    });
  };

  const updateAddProductSideBarIsOpen = isOpen => {
    if (!isClient()) {
      return;
    }
    modularPDPVar({
      ...modularPDPVar(),
      isModularAddProductSidebarOpen: isOpen,
    });
  };

  const updateModularBundleOrderStatus = useCallback(
    updatedModularOrder => {
      if (!isClient() || !updatedModularOrder) {
        return;
      }
      modularPDPVar({
        ...modularPDPVar(),
        modularBundleOrderStatus: updatedModularOrder,
      });
    },
    [modularPDPVar]
  );

  const updateModularTotalPrice = updatedPrice => {
    if (!isClient()) {
      return;
    }
    modularPDPVar({
      ...modularPDPVar(),
      modularTotalPrice: updatedPrice,
    });
  };

  return {
    data: {
      modularState,
    },
    actions: {
      setModularSlug,
      setModularPrice,
      setModularBundledProducts,
      setModularPreConfigProducts,
      setModularParentOrientationOptions,
      setModularSectionalProduct,
      updateAddProductSideBarIsOpen,
      updateModularBundleOrderStatus,
      updateModularTotalPrice,
      updateCurrentModularGalleryProduct,
      setModularCollectionTitle,
    },
  };
};

export default useModularPDPVar;
